import React from 'react';
import { motion } from 'framer-motion';
import { useTheme } from '../context/ThemeContext';
import { Project, Experience } from '../types';

const FreelanceProfile: React.FC = () => {
  const { themeConfig } = useTheme();

  // Freelance-focused projects
  const freelanceProjects: Project[] = [
    {
      id: 'legalease',
      title: 'LegalEase AI',
      description: 'Comprehensive legal research web application powered by MindsDB, Ollama, and Gemini AI. Provides intelligent legal document search and AI-powered legal assistance.',
      technologies: ['Python', 'Flask', 'MindsDB', 'Ollama', 'Gemini AI', 'PostgreSQL', 'Docker'],
      githubUrl: 'https://github.com/sharmachaitanya945/legalease',
      featured: true,
      imageUrl: '/assets/legalease.png',
    },
    {
      id: 'CareerAgent-AI-Powered-Job-Assistant',
      title: 'CareerAgent AI-Powered Job Assistant',
      description: 'AI-powered job assistant for career guidance, resume parsing, and job search automation with advanced NLP capabilities.',
      technologies: ['Python', 'AI', 'NLP', 'Machine Learning'],
      githubUrl: 'https://github.com/sharmachaitanya945/CareerAgent-AI-Powered-Job-Assistant',
      featured: true,
      imageUrl: '/assets/careerAgentAi.png',
    },
    {
      id: 'PDF2CSV-AI',
      title: 'PDF2CSV-AI',
      description: 'AI-powered tool to convert PDF bank statements to CSV. Uses deep learning for table extraction and document parsing.',
      technologies: ['Python', 'AI', 'Deep Learning', 'PDF Processing'],
      githubUrl: 'https://github.com/sharmachaitanya945/PDF2CSV-AI',
      featured: true,
      imageUrl: '/assets/pdf2Csv.png',
    },
    {
      id: 'face-recognition-system',
      title: 'Face Recognition System',
      description: 'Advanced computer vision project for real-time face detection, recognition, and analysis using deep learning techniques.',
      technologies: ['Python', 'OpenCV', 'Deep Learning', 'Computer Vision', 'TensorFlow'],
      githubUrl: 'https://github.com/sharmachaitanya945/face-recognition-system',
      imageUrl: '/assets/faceRecognitionSystem.png',
    },
    {
      id: 'practo-doctor-scraper',
      title: 'Practo Doctor Scraper',
      description: 'Web scraping tool to extract doctor information from Practo. Automates data collection for healthcare professionals.',
      technologies: ['Python', 'Web Scraping', 'Data Extraction', 'Automation'],
      githubUrl: 'https://github.com/sharmachaitanya945/practo-doctor-scraper',
      imageUrl: '/assets/practoDoctorScraper.png',
    },
    {
      id: '3d_model_generator',
      title: '3D Model Generator',
      description: 'Generate 3D models from data using Python, AI, and computer vision techniques.',
      technologies: ['Python', '3D Modeling', 'AI', 'Computer Vision'],
      githubUrl: 'https://github.com/sharmachaitanya945/3d_model_generator',
      imageUrl: '/assets/threeDModelGenerator.png',
    },
  ];

  // Freelance experience and achievements
  const freelanceExperience: Experience[] = [
    {
      id: 'upwork-freelancer',
      company: 'Upwork',
      position: 'Freelance Software Developer (Python/AI)',
      startDate: 'Jan 2024',
      endDate: 'Present',
      description: [
        'Completed 40+ hours with 100% Job Success Score',
        'Earned $1000+ across multiple AI/ML and Python projects',
        'Delivered Python automation tools for data extraction and ML preprocessing',
        'Built custom ML models for finance, healthcare, and automation sectors',
        'Developed ChatGPT-based Excel Prompt Maker App using GPT API',
        'Maintained 5-star client feedback and perfect completion rate',
      ],
      technologies: ['Python', 'AI/ML', 'GPT API', 'Excel Automation', 'Web Scraping'],
    },
  ];

  const achievements = [
    {
      title: 'Winner (1st Place) – ISRO Bhartiya Antariksh Hackathon 2025',
      description: 'Among 8,744 teams; awarded at Bharat Mandapam, New Delhi by ISRO Chairman & Dr. Jitendra Singh',
      date: '2025',
      icon: '🏆',
    },
    {
      title: 'Hackathon Mentor',
      description: 'Invited as mentor to guide participants in AI/ML development and problem-solving',
      date: '2024-2025',
      icon: '👨‍🏫',
    },
    {
      title: '100% Job Success Score on Upwork',
      description: 'Perfect track record with 5-star client feedback across all projects',
      date: '2024-Present',
      icon: '⭐',
    },
  ];

  const services = [
    {
      title: 'AI/ML Development',
      description: 'Custom machine learning models, deep learning solutions, and AI-powered applications',
      icon: '🤖',
      skills: ['TensorFlow', 'PyTorch', 'Scikit-learn', 'OpenCV'],
    },
    {
      title: 'Data Science & Analytics',
      description: 'Data analysis, visualization, predictive modeling, and business intelligence solutions',
      icon: '📊',
      skills: ['Pandas', 'NumPy', 'Matplotlib', 'Statistical Analysis'],
    },
    {
      title: 'Python Automation',
      description: 'Web scraping, data extraction, process automation, and workflow optimization',
      icon: '⚙️',
      skills: ['Python', 'Selenium', 'BeautifulSoup', 'APIs'],
    },
    {
      title: 'Computer Vision',
      description: 'Image processing, object detection, facial recognition, and video analysis',
      icon: '👁️',
      skills: ['OpenCV', 'YOLO', 'Deep Learning', 'Image Processing'],
    },
  ];

  const container = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const item = {
    hidden: { opacity: 0, y: 20 },
    show: { opacity: 1, y: 0 },
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className={`${themeConfig.layout?.sectionStyle} text-center`}>
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <h1 className="text-4xl lg:text-6xl font-bold mb-6 text-[var(--color-primary)]">
            Freelance AI/ML Developer
          </h1>
          <p className="text-xl lg:text-2xl mb-8 text-[var(--color-text)] opacity-90">
            Transforming Ideas into Intelligent Solutions
          </p>
          <div className="flex flex-wrap justify-center gap-6 mb-8">
            <div className="text-center">
              <div className="text-3xl font-bold text-[var(--color-primary)]">40+</div>
              <div className="text-sm text-[var(--color-text)] opacity-70">Hours Completed</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-[var(--color-primary)]">100%</div>
              <div className="text-sm text-[var(--color-text)] opacity-70">Job Success Score</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-[var(--color-primary)]">$1000+</div>
              <div className="text-sm text-[var(--color-text)] opacity-70">Earned</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-[var(--color-primary)]">⭐⭐⭐⭐⭐</div>
              <div className="text-sm text-[var(--color-text)] opacity-70">Client Rating</div>
            </div>
          </div>
          <div className="flex flex-wrap justify-center gap-4">
            <a
              href="https://www.upwork.com/freelancers/~01f16adc9d2f50d399"
              target="_blank"
              rel="noopener noreferrer"
              className="px-8 py-4 rounded-md bg-[var(--color-primary)] text-white font-medium text-lg hover:bg-opacity-90 transition"
            >
              View Upwork Profile
            </a>
            <a
              href="#contact"
              className="px-8 py-4 rounded-md border border-[var(--color-primary)] text-[var(--color-primary)] font-medium text-lg hover:bg-[var(--color-primary)] hover:text-white transition"
            >
              Hire Me
            </a>
          </div>
        </motion.div>
      </section>

      {/* Services Section */}
      <section className={`${themeConfig.layout?.sectionStyle}`}>
        <h2 className="text-3xl font-bold mb-2 text-[var(--color-primary)]">Services I Offer</h2>
        <p className="text-lg mb-10">Specialized solutions for your business needs</p>
        
        <motion.div
          className="grid grid-cols-1 md:grid-cols-2 gap-8"
          variants={container}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true }}
        >
          {services.map((service, idx) => (
            <motion.div
              key={idx}
              className="p-6 rounded-lg border border-[var(--color-secondary)] hover:shadow-lg transition-all"
              variants={item}
            >
              <div className="text-4xl mb-4">{service.icon}</div>
              <h3 className="text-xl font-semibold mb-3 text-[var(--color-primary)]">{service.title}</h3>
              <p className="text-[var(--color-text)] opacity-80 mb-4">{service.description}</p>
              <div className="flex flex-wrap gap-2">
                {service.skills.map((skill, skillIdx) => (
                  <span
                    key={skillIdx}
                    className="px-3 py-1 text-xs rounded-full bg-[var(--color-secondary)] bg-opacity-30"
                  >
                    {skill}
                  </span>
                ))}
              </div>
            </motion.div>
          ))}
        </motion.div>
      </section>

      {/* Achievements Section */}
      <section className={`${themeConfig.layout?.sectionStyle}`}>
        <h2 className="text-3xl font-bold mb-2 text-[var(--color-primary)]">Key Achievements</h2>
        <p className="text-lg mb-10">Recognition and milestones in my freelance journey</p>
        
        <motion.div
          className="space-y-6"
          variants={container}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true }}
        >
          {achievements.map((achievement, idx) => (
            <motion.div
              key={idx}
              className="flex items-start gap-4 p-6 rounded-lg border border-[var(--color-secondary)] hover:shadow-lg transition-all"
              variants={item}
            >
              <div className="text-3xl">{achievement.icon}</div>
              <div className="flex-1">
                <div className="flex flex-col md:flex-row md:justify-between md:items-start mb-2">
                  <h3 className="text-xl font-semibold text-[var(--color-primary)]">{achievement.title}</h3>
                  <span className="text-sm font-medium text-[var(--color-text)] opacity-70 mt-1 md:mt-0">
                    {achievement.date}
                  </span>
                </div>
                <p className="text-[var(--color-text)] opacity-80">{achievement.description}</p>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </section>

      {/* Featured Projects Section */}
      <section className={`${themeConfig.layout?.sectionStyle}`}>
        <h2 className="text-3xl font-bold mb-2 text-[var(--color-primary)]">Featured Projects</h2>
        <p className="text-lg mb-10">Professional projects showcasing my expertise</p>
        
        <motion.div
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
          variants={container}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true }}
        >
          {freelanceProjects.map((project) => (
            <motion.div
              key={project.id}
              className="rounded-lg overflow-hidden border border-[var(--color-secondary)] hover:shadow-xl transition-all"
              variants={item}
            >
              <div className="relative h-48 overflow-hidden">
                <img
                  src={project.imageUrl || 'https://via.placeholder.com/500x300?text=Project+Image'}
                  alt={project.title}
                  className="w-full h-full object-cover"
                />
                {project.featured && (
                  <div className="absolute top-3 right-3 bg-[var(--color-primary)] text-white text-xs px-2 py-1 rounded-full">
                    Featured
                  </div>
                )}
              </div>
              <div className="p-6">
                <h3 className="text-xl font-bold mb-2">{project.title}</h3>
                <p className="text-sm mb-4 text-[var(--color-text)] opacity-80">
                  {project.description}
                </p>
                <div className="mb-6 flex flex-wrap gap-2">
                  {project.technologies.map((tech, index) => (
                    <span
                      key={index}
                      className="inline-block text-xs px-2 py-1 rounded-full bg-[var(--color-secondary)] bg-opacity-30"
                    >
                      {tech}
                    </span>
                  ))}
                </div>
                <div className="flex justify-between">
                  {project.githubUrl && (
                    <a
                      href={project.githubUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="text-sm font-medium flex items-center text-[var(--color-primary)] hover:underline"
                    >
                      <svg className="h-4 w-4 mr-1" fill="currentColor" viewBox="0 0 24 24">
                        <path fillRule="evenodd" d="M12 2C6.477 2 2 6.484 2 12.017c0 4.425 2.865 8.18 6.839 9.504.5.092.682-.217.682-.483 0-.237-.008-.868-.013-1.703-2.782.605-3.369-1.343-3.369-1.343-.454-1.158-1.11-1.466-1.11-1.466-.908-.62.069-.608.069-.608 1.003.07 1.531 1.032 1.531 1.032.892 1.53 2.341 1.088 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.113-4.555-4.951 0-1.093.39-1.987 1.029-2.686-.103-.253-.446-1.272.098-2.65 0 0 .84-.27 2.75 1.025A9.564 9.564 0 0112 6.844c.85.004 1.705.115 2.504.337 1.909-1.295 2.748-1.025 2.748-1.025.546 1.378.202 2.397.1 2.65.64.699 1.028 1.593 1.028 2.686 0 3.847-2.338 4.695-4.566 4.944.359.309.678.919.678 1.852 0 1.336-.012 2.415-.012 2.744 0 .268.18.579.688.481C21.138 20.194 24 16.44 24 12.017 24 6.484 19.523 2 12 2z" clipRule="evenodd" />
                      </svg>
                      View Code
                    </a>
                  )}
                </div>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </section>

      {/* Freelance Experience Section */}
      <section className={`${themeConfig.layout?.sectionStyle}`}>
        <h2 className="text-3xl font-bold mb-2 text-[var(--color-primary)]">Freelance Journey</h2>
        <p className="text-lg mb-10">My professional freelancing experience and client work</p>
        
        <motion.div
          className="space-y-8"
          variants={container}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true }}
        >
          {freelanceExperience.map((exp) => (
            <motion.div
              key={exp.id}
              className="p-6 rounded-lg border border-[var(--color-secondary)] hover:shadow-lg transition-all"
              variants={item}
            >
              <div className="flex flex-col md:flex-row md:justify-between md:items-start mb-4">
                <div>
                  <h3 className="text-2xl font-semibold text-[var(--color-primary)]">{exp.position}</h3>
                  <p className="text-lg text-[var(--color-text)] opacity-80">{exp.company}</p>
                </div>
                <span className="text-sm font-medium text-[var(--color-primary)] mt-2 md:mt-0">
                  {exp.startDate} - {exp.endDate}
                </span>
              </div>
              <ul className="list-disc list-inside space-y-2 mb-6">
                {exp.description.map((desc, idx) => (
                  <li key={idx} className="text-[var(--color-text)] opacity-80">{desc}</li>
                ))}
              </ul>
              <div className="flex flex-wrap gap-2">
                {exp.technologies?.map((tech, idx) => (
                  <span
                    key={idx}
                    className="px-3 py-1 text-xs rounded-full bg-[var(--color-secondary)] bg-opacity-30"
                  >
                    {tech}
                  </span>
                ))}
              </div>
            </motion.div>
          ))}
        </motion.div>
      </section>

      {/* Call to Action */}
      <section className={`${themeConfig.layout?.sectionStyle} text-center`}>
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="max-w-4xl mx-auto"
        >
          <h2 className="text-3xl font-bold mb-6 text-[var(--color-primary)]">Ready to Work Together?</h2>
          <p className="text-lg mb-8 text-[var(--color-text)] opacity-80">
            Let's discuss your project requirements and bring your ideas to life with cutting-edge AI/ML solutions.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <a
              href="/contact"
              className="px-8 py-4 rounded-md bg-[var(--color-primary)] text-white font-medium text-lg hover:bg-opacity-90 transition"
            >
              Start a Project
            </a>
            <a
              href="mailto:sharmachaitanya945@gmail.com"
              className="px-8 py-4 rounded-md border border-[var(--color-primary)] text-[var(--color-primary)] font-medium text-lg hover:bg-[var(--color-primary)] hover:text-white transition"
            >
              Email Me Directly
            </a>
          </div>
        </motion.div>
      </section>
    </div>
  );
};

export default FreelanceProfile;
